import { ErrorHandler, inject, Service } from '@angular/core';

import { BaasLoggingService, BaasTelemetry } from '@lvflanders/bar';

import { BAR_CORE_CONSTANTS } from './constants/bar-core.constants';
import { BAR_ERROR_HANDLER } from './constants/internal-tokens.constants';
import { BAAS_TELEMETRY } from './constants/tokens.constants';
import { BarErrorHandlerHook, BarErrorHandlerHookArgs } from './hooks/bar-error-handler.hook';
import { BarCoreService } from './services/bar-core.service';

@Service({ autoProvided: false })
// https://medium.com/@amcdnl/global-error-handling-with-angular2-6b992bdfb59c
export class BarErrorHandler implements ErrorHandler {
    readonly #core = inject(BarCoreService);
    readonly #logging = inject(BaasLoggingService, { optional: true });
    readonly #errorHandlerHooks = inject<BarErrorHandlerHook[]>(BAR_ERROR_HANDLER, { optional: true });
    readonly #telemetry = inject<BaasTelemetry>(BAAS_TELEMETRY, { optional: true });

    handleError(error: any) {
        if (error == null) return;

        if (typeof error === 'object') {
            error.handled = true;
        }

        let proceed = true;

        // process angular hooks
        if (this.#errorHandlerHooks && this.#errorHandlerHooks.length) {
            this.#errorHandlerHooks.forEach((hook) => {
                const args: BarErrorHandlerHookArgs = {
                    handled: false,
                    error: error
                };
                hook.run(args);

                if (args.handled) proceed = false;
            });
        }
        // a hook already handled the error, stop proceeding
        if (!proceed) return;
        // do something with the exception
        try {
            console.error(error);

            const getCircularReplacer = () => {
                const seen = new WeakSet();
                return (key, value) => {
                    if (typeof value === 'object' && value !== null) {
                        if (seen.has(value)) {
                            return;
                        }
                        seen.add(value);
                    }
                    return value;
                };
            };

            // put the error in session storage
            window.sessionStorage[BAR_CORE_CONSTANTS.STORAGE_KEY_PREFIX + 'error'] = JSON.stringify(error, getCircularReplacer());
            this.#core.errorOccurred.emit(error);
            if (this.#logging != null) {
                this.#logging.error({ error });
            }
            // telemetry is optional, for example polaris doesn't have telemetry
            if (this.#telemetry) this.#telemetry.handleError(error);
        } catch (error) {
            console.error(error);
        }
    }
}
